import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { ASSETS, MOCK_PRODUCTS, FEATURE_HIGHLIGHTS } from "@/data/mockData";
import { Container, Section, Overline } from "@/components/ui-blocks";
import ProductCard, { StockBadge } from "@/components/ProductCard";
import BulkOffers from "@/components/BulkOffers";
import { zoneById, deliverySentence, leadTimeSentences } from "@/lib/deliveryCopy";
import { CalendarClock, Globe2, Package, Truck } from "lucide-react";
import Seo from "@/components/Seo";
import { isOrderable, orderCtaLabel } from "@/lib/stock";
import { fetchProductById, fetchProducts, fetchPricing } from "@/services/api";

const BOOK_ID = "bk-chettis-01";

export default function FeaturedBook() {
  const fallback = MOCK_PRODUCTS.find((p) => p.id === BOOK_ID);
  // Live product so price, ISBN and stock status match the admin console
  // (falls back to the bundled copy if Firestore is unreachable).
  const [book, setBook] = useState(fallback);
  const [related, setRelated] = useState(
    MOCK_PRODUCTS.filter((p) => p.id !== BOOK_ID).slice(0, 4)
  );
  const [pricing, setPricing] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProductById(BOOK_ID)
      .then((p) => { if (p) setBook(p); })
      .catch(() => {});
    fetchProducts()
      .then((list) => {
        const rest = list.filter((p) => p.id !== BOOK_ID).slice(0, 4);
        if (rest.length) setRelated(rest);
      })
      .catch(() => {});
    fetchPricing().then(setPricing).catch(() => {});
  }, []);

  // Every delivery claim on this page comes from the rules, so none of it can
  // contradict what checkout charges.
  const sgZone = zoneById(pricing, "singapore");
  const localDelivery = {
    zoneLabel: sgZone?.label || "Singapore",
    rate: Number(sgZone?.rate ?? book.shippingCost ?? 0),
    per: sgZone?.per || "per copy",
  };
  const deliveryBlurb = deliverySentence(pricing);
  const leadTimes = leadTimeSentences(pricing);

  return (
    <div data-testid="featured-book-page">
      <Seo title="The Chettis of South-east Asia" description="The featured book from Ace Asia Productions — a landmark work on the Chetti Melaka community, written and published by Ms Sajan." />
      {/* Hero — book on dark stage */}
      <section className="relative bg-[#281a12] text-[#FDFBF7] grain overflow-hidden" data-testid="featured-book-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Overline className="text-[#946f36] mb-6">
              <span className="bg-[#946f36]" /> The Featured Volume
            </Overline>
            <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[1.02]">
              The Chettis of South-east Asia
            </h1>
            <p className="font-serif-display italic text-2xl text-[#946f36] mt-3">
              by Chantal Sajan
            </p>
            <p className="mt-6 text-[#D1C3B4] text-lg leading-relaxed max-w-xl">
              A ground-breaking new exploration of one of South-east Asia&apos;s
              most fascinating yet little-known communities, written and
              published by Ms Sajan. Discover the untold story of the Chetti
              Melaka — the Indian Peranakans of
              Singapore and Malaysia — and their remarkable connections to
              trade, culture and civilisation stretching back more than 2,000
              years.
            </p>

            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <StockBadge status={book.stockStatus} />
              <span className="text-xs text-[#D1C3B4]">
                {isOrderable(book)
                  ? "Now available · Ships within Singapore & to ASEAN"
                  : "Launching September 2026 · Ships within Singapore & to ASEAN"}
              </span>
            </div>

            <p className="mt-4 text-xs text-[#D1C3B4] leading-relaxed max-w-xl">
              Price is SGD {Number(book.price || 0).toFixed(2)}, excluding
              shipping. Local delivery within Singapore is SGD{" "}
              {localDelivery.rate.toFixed(2)} {localDelivery.per}. Overseas
              delivery is calculated at checkout.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="featured-book-preorder-button"
                disabled={!isOrderable(book)}
                onClick={() => addItem(book, 1)}
                className={`px-7 py-4 text-xs uppercase tracking-[0.2em] transition-colors ${
                  isOrderable(book)
                    ? "bg-[#946f36] hover:bg-[#FDFBF7] hover:text-[#281a12] text-[#281a12]"
                    : "border border-[#946f36]/50 text-[#946f36] cursor-not-allowed"
                }`}
              >
                {isOrderable(book)
                  ? `${orderCtaLabel(book)} — SGD ${book.price.toFixed(2)}`
                  : orderCtaLabel(book)}
              </button>
              <Link
                to={`/product/${book.id}`}
                data-testid="featured-book-detail-link"
                className="border border-[#946f36] text-[#946f36] hover:bg-[#946f36] hover:text-[#281a12] px-7 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                View Product Page
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative max-w-lg mx-auto">
              <div className="absolute -inset-6 border border-[#946f36]/40 pointer-events-none" />
              <img
                src={book.image || ASSETS.bookCoverLight}
                alt={book.title}
                data-testid="featured-book-cover-image"
                className="relative w-full object-cover shadow-2xl"
              />
              <p
                data-testid="featured-book-isbn"
                className="relative mt-5 text-center text-xs tracking-wide text-[#D1C3B4]"
              >
                International Standard Book No: ISBN {book.isbn}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bulk offers — high on the page, since schools and libraries are the
          buyers these tiers exist for. */}
      <Section className="bg-[#F4F1EA]">
        <Container>
          <BulkOffers product={book} />
        </Container>
      </Section>

      {/* Story spans */}
      <Section className="bg-[#FDFBF7]">
        <Container>
          <div className="max-w-3xl">
            <Overline>The Argument</Overline>
            <h2 className="mt-4 font-serif-display text-4xl md:text-6xl font-light leading-[1.04]">
              A story that spans continents and centuries.
            </h2>
            <p className="mt-6 text-[#4A3728] text-lg leading-relaxed">
              Far more than a community history, <em>The Chettis of South-east
              Asia</em> uncovers extraordinary links between South-east Asia
              and ancient global trade routes.
            </p>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#6e5022]/20">
            {FEATURE_HIGHLIGHTS.map((f, i) => (
              <div
                key={f.title}
                className="bg-[#FDFBF7] p-8 hover:bg-[#F4F1EA] transition-colors"
                data-testid={`featured-book-highlight-${i}`}
              >
                <div className="overline text-[#946f36]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-3 font-serif-display text-2xl text-[#281a12]">
                  {f.title}
                </div>
                <p className="mt-2 text-sm text-[#4A3728] leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Product Info strip */}
      <Section className="bg-[#F4F1EA]">
        <Container>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <InfoStat icon={Package} label="Format" value="Paperback" />
            <InfoStat icon={CalendarClock} label="Launching" value="September 2026" />
            <InfoStat
              icon={Truck}
              label={`Delivery · ${localDelivery.zoneLabel}`}
              value={`SGD ${localDelivery.rate.toFixed(2)}`}
            />
            <InfoStat icon={Globe2} label="Pages" value="350" />
          </div>
          <div className="mt-10 text-sm text-[#4A3728] max-w-3xl">
            Online orders available from late September 2026.
            {deliveryBlurb ? ` We deliver to ${deliveryBlurb}.` : ""}
          </div>
          {leadTimes.length > 0 && (
            <ul className="mt-3 text-sm text-[#946f36] max-w-3xl space-y-1" data-testid="featured-book-lead-times">
              {leadTimes.map((line) => <li key={line}>{line}</li>)}
            </ul>
          )}
        </Container>
      </Section>

      {/* Related */}
      <Section className="bg-[#FDFBF7]">
        <Container>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <h2 className="font-serif-display text-3xl md:text-4xl font-light">
              From the Ace Asia catalogue.
            </h2>
            <Link to="/shop" data-testid="featured-book-view-all" className="overline text-[#6e5022] hover:text-[#946f36]">
              View shop →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </Container>
      </Section>
    </div>
  );
}

function InfoStat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#6e5022]/25 bg-[#FDFBF7] p-6">
      <Icon className="w-5 h-5 text-[#6e5022]" strokeWidth={1.5} />
      <div className="mt-3 overline text-[#946f36]">{label}</div>
      <div className="mt-1 font-serif-display text-2xl text-[#281a12]">{value}</div>
    </div>
  );
}
